// ============================================================
//  src/modal.js — Modal de formulario para agregar/editar concursos
//  Maneja apertura, cierre, validación y envío del formulario.
// ============================================================

import { agregarEdicion, actualizarEdicion,
         guardarFolderIds }                   from './firestore.js';
import { mostrarToast, toggleBoton }         from './ui.js';
import { auth }                              from './firebase.js';
import { getPerfilActual }                   from './perfil.js';
import { tienePermisosDrive,
         crearEstructuraCarpetas }           from './drive.js';
import { t }                                 from './i18n.js';
import { inicializarBuscador,
         resetearSeleccion,
         cerrarDropdown }                    from './catalogo.js';
import { abrirModalSolicitud }               from './solicitud.js';

// ID del documento que se está editando (null = modo crear)
let idEdicionActual = null;

// Modos de operación disponibles
const MODOS = { CREAR: 'crear', EDITAR: 'editar' };

// ============================================================
//  Apertura del modal
// ============================================================

/**
 * Abre el modal en modo "crear" (formulario vacío).
 */
export function abrirModalCrear() {
    idEdicionActual = null;
    limpiarFormulario();
    cargarOpcionesIndicativo(null); // null = seleccionar el default
    actualizarTituloModal(MODOS.CREAR);
    mostrarModal();
    // Inicializar buscador del catálogo
    inicializarBuscador(_abrirModalSolicitud);
}

/**
 * Abre el modal en modo "editar" con los datos de una edición.
 * @param {Object} edicion - Datos del concurso a editar
 */
export function abrirModalEditar(edicion) {
    idEdicionActual = edicion.id;
    cargarDatosEnFormulario(edicion);
    cargarOpcionesIndicativo(edicion.indicativo ?? null);
    actualizarTituloModal(MODOS.EDITAR);
    mostrarModal();
    // En modo editar el buscador está deshabilitado
    const campoConcurso = document.getElementById('campo-concurso');
    if (campoConcurso) campoConcurso.readOnly = true;
}

// ============================================================
//  Control del modal
// ============================================================

function mostrarModal() {
    const modal = document.getElementById('modal-concurso');
    modal.classList.add('modal--visible');
    // Foco al primer campo para accesibilidad
    setTimeout(() => document.getElementById('campo-concurso')?.focus(), 50);
}

export function cerrarModal() {
    const modal = document.getElementById('modal-concurso');
    modal.classList.remove('modal--visible');
    idEdicionActual = null;
    cerrarDropdown();
    resetearSeleccion();
    // Restaurar campo nombre como editable
    const campoConcurso = document.getElementById('campo-concurso');
    if (campoConcurso) campoConcurso.readOnly = false;
}

function actualizarTituloModal(modo) {
    const titulo = document.getElementById('modal-titulo');
    if (titulo) {
        titulo.textContent = modo === MODOS.EDITAR
            ? t('modal.concurso.titulo.editar')
            : t('modal.concurso.titulo.nuevo');
    }
}

// ============================================================
//  Gestión del formulario
// ============================================================

/**
 * Limpia todos los campos del formulario.
 */
function limpiarFormulario() {
    const form = document.getElementById('form-concurso');
    if (form) form.reset();
    limpiarErrores();
}

/**
 * Carga los datos de una edición en los campos del formulario.
 * @param {Object} edicion
 */
function cargarDatosEnFormulario(edicion) {
    limpiarErrores();

    setValorCampo('campo-concurso',     edicion.concurso ?? '');
    setValorCampo('campo-año',          edicion.año ?? '');
    setValorCampo('campo-modo',         edicion.modo ?? '');
    setValorCampo('campo-web-url',      edicion.webUrl ?? '');
    setValorCampo('campo-raw-score',    edicion.rawScore > 0 ? edicion.rawScore : '');
    setValorCampo('campo-final-score',  edicion.finalScore > 0 ? edicion.finalScore : '');

    // Posición
    const pos = edicion.posicion ?? {};
    setValorCampo('pos-local-puesto',          pos.local?.puesto ?? '');
    setValorCampo('pos-local-pais',            pos.local?.ambito ?? '');
    setValorCampo('pos-regional-puesto',       pos.regional?.puesto ?? '');
    setValorCampo('pos-regional-continente',   pos.regional?.ambito ?? '');
    setValorCampo('pos-mundial-puesto',        pos.mundial?.puesto ?? '');

    // Convertir Date a formato datetime-local (YYYY-MM-DDTHH:mm)
    if (edicion.fechaInicio) {
        setValorCampo('campo-fecha-inicio', fechaADatetimeLocal(edicion.fechaInicio));
    }
    if (edicion.fechaFin) {
        setValorCampo('campo-fecha-fin', fechaADatetimeLocal(edicion.fechaFin));
    }
}

function setValorCampo(id, valor) {
    const campo = document.getElementById(id);
    if (campo) campo.value = valor;
}

/**
 * Rellena el select de indicativos con los del perfil del usuario.
 * Prioridad por defecto:
 * 1) primer indicativo especial
 * 2) indicativo operador
 * Compatibilidad:
 * - indicativosEspeciales (array nuevo)
 * - indicativoEspecial (string viejo)
 * @param {string|null} valorActual - Indicativo ya guardado (modo editar) o null
 */
function cargarOpcionesIndicativo(valorActual) {
    const select = document.getElementById('campo-indicativo');
    if (!select) return;

    const perfil = getPerfilActual();
    const opciones = [];

    if (Array.isArray(perfil?.indicativosEspeciales)) {
        perfil.indicativosEspeciales.forEach(ind => {
            const limpio = String(ind ?? '').trim().toUpperCase();
            if (limpio) opciones.push(limpio);
        });
    }

    if (perfil?.indicativoOperador?.trim()) {
        opciones.push(perfil.indicativoOperador.trim().toUpperCase());
    }

    // Limpiar opciones existentes de forma segura
    select.replaceChildren();

    // Si no hay ninguno en el perfil, mostrar placeholder
    if (opciones.length === 0) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = t('modal.concurso.select.sinperfil');
        select.appendChild(option);
        return;
    }

    // Eliminar duplicados preservando orden
    const unicas = [...new Set(opciones)];

    unicas.forEach(ind => {
        const option = document.createElement('option');
        option.value = ind;
        option.textContent = ind;
        select.appendChild(option);
    });

    // En modo editar: seleccionar el indicativo guardado
    // En modo crear: seleccionar el primero de la lista
    const valorActualNormalizado = String(valorActual ?? '').trim().toUpperCase();

    if (valorActualNormalizado && unicas.includes(valorActualNormalizado)) {
        select.value = valorActualNormalizado;
    } else {
        select.value = unicas[0];
    }
}

/**
 * Convierte un objeto Date al formato que acepta datetime-local.
 * @param {Date} fecha
 * @returns {string} "YYYY-MM-DDTHH:mm"
 */
function fechaADatetimeLocal(fecha) {
    if (!fecha) return '';
    const año  = fecha.getFullYear();
    const mes  = String(fecha.getMonth() + 1).padStart(2, '0');
    const dia  = String(fecha.getDate()).padStart(2, '0');
    const hora = String(fecha.getHours()).padStart(2, '0');
    const min  = String(fecha.getMinutes()).padStart(2, '0');
    return `${año}-${mes}-${dia}T${hora}:${min}`;
}

// ============================================================
//  Solicitud de nuevo concurso
// ============================================================

/**
 * Abre el modal de solicitud de nuevo concurso.
 * Se llama desde el buscador cuando el usuario hace clic en "Solicitar".
 * La implementación completa está en solicitud.js (Paso 5).
 * @param {string} nombrePrecargado - Texto que escribió el usuario en la búsqueda
 */
function _abrirModalSolicitud(nombrePrecargado) {
    abrirModalSolicitud(nombrePrecargado);
}

// ============================================================
//  Validación
// ============================================================

/**
 * Valida los campos obligatorios del formulario.
 * Muestra mensajes de error inline por campo.
 * @returns {Object|null} Datos del formulario o null si hay errores
 */
function validarFormulario() {
    limpiarErrores();
    let valido = true;

    const concurso    = document.getElementById('campo-concurso')?.value.trim() ?? '';
    const año         = document.getElementById('campo-año')?.value ?? '';
    const modo        = document.getElementById('campo-modo')?.value ?? '';
    const indicativo  = document.getElementById('campo-indicativo')?.value ?? '';
    const fechaInicio = document.getElementById('campo-fecha-inicio')?.value ?? '';
    const fechaFin    = document.getElementById('campo-fecha-fin')?.value ?? '';
    const webUrl      = document.getElementById('campo-web-url')?.value.trim() ?? '';
    const rawScore    = document.getElementById('campo-raw-score')?.value ?? '';
    const finalScore  = document.getElementById('campo-final-score')?.value ?? '';

    // Posición
    const posLocalPuesto         = document.getElementById('pos-local-puesto')?.value.trim() ?? '';
    const posLocalPais           = document.getElementById('pos-local-pais')?.value.trim() ?? '';
    const posRegionalPuesto      = document.getElementById('pos-regional-puesto')?.value.trim() ?? '';
    const posRegionalContinente  = document.getElementById('pos-regional-continente')?.value.trim() ?? '';
    const posMundialPuesto       = document.getElementById('pos-mundial-puesto')?.value.trim() ?? '';

    if (!concurso) {
        mostrarErrorCampo('campo-concurso', t('modal.concurso.error.nombre'));
        valido = false;
    }
    if (!año || isNaN(año) || año < 1990 || año > 2100) {
        mostrarErrorCampo('campo-año', t('modal.concurso.error.año'));
        valido = false;
    }
    if (!modo) {
        mostrarErrorCampo('campo-modo', t('modal.concurso.error.modo'));
        valido = false;
    }
    if (!indicativo) {
        mostrarErrorCampo('campo-indicativo', t('modal.concurso.error.indicativo'));
        valido = false;
    }
    if (!fechaInicio) {
        mostrarErrorCampo('campo-fecha-inicio', t('modal.concurso.error.fechainicio'));
        valido = false;
    }
    if (!fechaFin) {
        mostrarErrorCampo('campo-fecha-fin', t('modal.concurso.error.fechafin'));
        valido = false;
    }
    if (fechaInicio && fechaFin && new Date(fechaInicio) >= new Date(fechaFin)) {
        mostrarErrorCampo('campo-fecha-fin', t('modal.concurso.error.fechaorden'));
        valido = false;
    }
    if (webUrl && !esUrlValida(webUrl)) {
        mostrarErrorCampo('campo-web-url', t('modal.concurso.error.url'));
        valido = false;
    }

    if (!valido) return null;

    return {
        concurso,
        año: parseInt(año, 10),
        modo,
        indicativo,
        fechaInicio,
        fechaFin,
        webUrl,
        rawScore: rawScore ? parseInt(rawScore, 10) : 0,
        finalScore: finalScore ? parseInt(finalScore, 10) : 0,
        posicion: _construirPosicion(
            posLocalPuesto,
            posLocalPais,
            posRegionalPuesto,
            posRegionalContinente,
            posMundialPuesto
        ),
    };
}

function mostrarErrorCampo(idCampo, mensaje) {
    const campo = document.getElementById(idCampo);
    if (!campo) return;
    campo.classList.add('campo--error');
    const errorEl = document.getElementById(`error-${idCampo.replace('campo-', '')}`);
    if (errorEl) {
        errorEl.textContent = mensaje;
        errorEl.classList.add('visible');
    }
}

function limpiarErrores() {
    document.querySelectorAll('.campo--error').forEach(el => el.classList.remove('campo--error'));
    document.querySelectorAll('.campo-error-msg').forEach(el => {
        el.textContent = '';
        el.classList.remove('visible');
    });
}

function esUrlValida(url) {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
}

// ============================================================
//  Envío del formulario
// ============================================================

/**
 * Construye el objeto posicion a partir de los valores del formulario.
 * Solo incluye los niveles donde se ingresó al menos el puesto.
 */
function _construirPosicion(localPuesto, localPais, regPuesto, regContinente, mundPuesto) {
    const pos = {};

    if (localPuesto) {
        pos.local = {
            puesto: parseInt(localPuesto, 10),
            ambito: localPais.toUpperCase() || '',
        };
    }

    if (regPuesto) {
        pos.regional = {
            puesto: parseInt(regPuesto, 10),
            ambito: regContinente.toUpperCase() || '',
        };
    }

    if (mundPuesto) {
        pos.mundial = {
            puesto: parseInt(mundPuesto, 10),
            ambito: 'WW',
        };
    }

    return Object.keys(pos).length > 0 ? pos : null;
}

export async function manejarSubmitFormulario(evento) {
    evento.preventDefault();

    const datos = validarFormulario();
    if (!datos) return;

    const usuario = auth.currentUser;
    if (!usuario) {
        mostrarToast(t('toast.sesion.expirada'), 'error');
        return;
    }

    toggleBoton('btn-guardar-concurso', false, t('common.guardando'));

    try {
        if (idEdicionActual) {
            // ── Modo editar ────────────────────────────────────
            const actualizado = await actualizarEdicion(idEdicionActual, datos);
            if (actualizado) {
                mostrarToast(t('toast.concurso.actualizado', { nombre: datos.concurso }), 'exito');
                cerrarModal();
                // Background: recrear carpetas si el modo/indicativo cambió
                crearCarpetasEnBackground(idEdicionActual, datos);
            }
        } else {
            // ── Modo crear ─────────────────────────────────────
            const id = await agregarEdicion(usuario.uid, datos);
            if (id) {
                mostrarToast(t('toast.concurso.agregado', { nombre: datos.concurso }), 'exito');
                cerrarModal();
                // Background: crear carpetas si hay token Drive activo
                crearCarpetasEnBackground(id, datos);
            }
        }
    } finally {
        toggleBoton('btn-guardar-concurso', true);
    }
}

/**
 * Crea la estructura de carpetas en Drive y guarda los IDs en Firestore.
 * Se ejecuta en background — no bloquea la UI ni el cierre del modal.
 * Solo actúa si el usuario ya autorizó Drive (token en memoria).
 *
 * @param {string} idEdicion - ID del documento en Firestore
 * @param {Object} datos - Datos del concurso (concurso, año, modo, indicativo)
 */
async function crearCarpetasEnBackground(idEdicion, datos) {
    // Si no hay token Drive, no hacemos nada —
    // las carpetas se crearán al subir el primer archivo
    if (!tienePermisosDrive()) {
        console.log('[Modal] Sin token Drive — carpetas se crearán al primer archivo.');
        return;
    }

    try {
        console.log('[Modal] Creando estructura Drive en background...');
        const folderIds = await crearEstructuraCarpetas(datos);
        await guardarFolderIds(idEdicion, folderIds);
        mostrarToast(t('toast.drive.carpetas.ok'), 'exito');
    } catch (error) {
        // Error silencioso — no interrumpir al usuario
        // Las carpetas se intentarán crear al primer archivo
        console.warn('[Modal] Error al crear carpetas Drive en background:', error);
        mostrarToast(t('toast.drive.carpetas.error'), 'info');
    }
}